/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return,
@typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */
import type { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

// Extended interface that includes both browser and server APIs
type ExtendedNewRelicAgent = BrowserAgent & {
  addCustomAttribute: (key: string, value: string | number | boolean) => void;
  recordCustomEvent: (
    eventType: string,
    attributes: Record<string, string | number | boolean>,
  ) => void;
};

/**
 * A wrapper for NewRelic that works in both client and server environments.
 * Uses the server-side NewRelic agent for SSR and browser agent for CSR.
 */
const newrelicWrapper = new Proxy({} as ExtendedNewRelicAgent, {
  get: (_target, prop) => {
    // For server-side, use the actual server-side NewRelic agent
    if (typeof window === 'undefined') {
      // Use static import for server-side NewRelic
      // eslint-disable-next-line global-require, @typescript-eslint/no-require-imports
      const serverNewRelic = require('newrelic');

      const serverFunction = serverNewRelic[prop];
      if (!serverFunction) {
        if (serverNewRelic.noticeError) {
          return () =>
            serverNewRelic.noticeError(
              new Error(
                `Server Side Request for NewRelic function that was not found: ${prop.toString()}`,
              ),
            );
        }
        return () => {
          console.error(
            `Server Side Request for NewRelic function that was not found: ${prop.toString()}`,
          );
        };
      }

      return serverNewRelic[prop];
    }

    // For client-side, return the actual property/method from window.newrelic
    // Handle cases where window.newrelic might not be available (like in tests)
    if (window.newrelic) {
      if (window.newrelic[prop]) {
        return window.newrelic[prop];
      }
      return () =>
        window.newrelic.noticeError(
          new Error(
            `Client Side Request for NewRelic function that was not found: ${prop.toString()}`,
          ),
        );
    }

    // Fallback for test environments or when NewRelic is not loaded
    return () => {};
  },
});

export default newrelicWrapper;
